import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { LayoutDashboard, FileText, StickyNote, LogOut, Search, Plus, X, Menu, ChevronDown, ChevronRight } from 'lucide-react';
import { collection, query, getDocs, limit, orderBy } from "firebase/firestore";
import useClickOutside from './hooks/useClickOutside.js';
import Logo from './logo.png';
import { TransactionModal, AccountsInvoiceModal } from './AccountsModals.js';

const Layout = ({ children, user, handleLogout, db }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState({ 
    quotes: [], 
    invoices: [], 
    orders: [], 
    accounts_invoices: [], 
    accounts_transactions: [] 
  });
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useClickOutside(() => setShowResults(false));
  
  // Responsive Sidebar State
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const sidebarRef = useClickOutside(() => {
    if (windowWidth <= 768) setIsSidebarOpen(false);
  });

  const [showSalesQuickActions, setShowSalesQuickActions] = useState(false);
  const quickActionsRef = useClickOutside(() => setShowSalesQuickActions(false));

  // State for Accounts Quick Actions
  const [showAccTransModal, setShowAccTransModal] = useState(false);
  const [showAccInvModal, setShowAccInvModal] = useState(false);

  // Collapsible Sections State
  const [expandedSections, setExpandedSections] = useState({
    Outwards: true,
    Inwards: true,
    Inventory: true
  });

  const toggleSection = (name) => {
    setExpandedSections(prev => ({ ...prev, [name]: !prev[name] }));
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 768) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar on navigation (mobile only)
  useEffect(() => {
    if (windowWidth <= 768) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.length >= 2) {
        performSearch();
      } else {
        setResults({ 
          quotes: [], 
          invoices: [], 
          orders: [], 
          accounts_invoices: [], 
          accounts_transactions: [] 
        });
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const performSearch = async () => {
    setIsSearching(true);
    setShowResults(true);
    const term = searchTerm.toLowerCase();

    try {
      // Define collections based on role
      let searchCollections = [];
      if (user?.role === 'ops') {
        searchCollections = ['orders'];
      } else if (user?.role === 'accounts') {
        searchCollections = ['accounts_invoices', 'accounts_transactions'];
      } else if (user?.role === 'admin') {
        searchCollections = ['quotes', 'invoices', 'orders', 'accounts_invoices', 'accounts_transactions'];
      } else {
        searchCollections = ['quotes', 'invoices', 'orders'];
      }

      const searchResults = { quotes: [], invoices: [], orders: [], accounts_invoices: [], accounts_transactions: [] };

      for (const colName of searchCollections) {
        const q = query(collection(db, colName), orderBy('createdAt', 'desc'), limit(100));
        const snapshot = await getDocs(q);
        const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        searchResults[colName] = docs.filter(doc => {
          if (colName === 'accounts_invoices') {
            return doc.customerName?.toLowerCase().includes(term) ||
                   doc.invoiceNo?.toLowerCase().includes(term);
          }
          if (colName === 'accounts_transactions') {
            return doc.category?.toLowerCase().includes(term) ||
                   doc.entityName?.toLowerCase().includes(term) ||
                   doc.remark?.toLowerCase().includes(term) ||
                   doc.invoiceNo?.toLowerCase().includes(term);
          }
          // Default for quotes, invoices, orders
          return doc.customerName?.toLowerCase().includes(term) ||
                 doc.quoteNumber?.toLowerCase().includes(term) ||
                 doc.voucherNo?.toLowerCase().includes(term) ||
                 doc.orderNumber?.toLowerCase().includes(term) ||
                 doc.subject?.toLowerCase().includes(term);
        }).slice(0, 5);
      }

      setResults(searchResults);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleResultClick = (path) => {
    setSearchTerm('');
    setShowResults(false);
    navigate(path);
  };

  const menuItems = [
    { name: 'Dashboard', icon: '🏠', path: '/', roles: ['sales', 'ops'] }, // Only sales/ops see regular dashboard
    { name: 'Quotes', icon: '📝', path: '/quotes', roles: ['sales'] },
    { name: 'Invoices', icon: '🧾', path: '/invoices', roles: ['sales'] },
    { name: 'All Orders', icon: '📖', path: '/orders', roles: ['sales', 'ops'] },
    
    { 
      type: 'section', 
      name: 'Outwards', 
      roles: ['ops'],
      children: [
        { name: 'Orders Que', icon: '📝', path: '/orders/confirmed', roles: ['sales', 'ops'] },
        { name: 'Material Out Checklist', icon: '📋', path: '/ops/shipments', roles: ['ops'] },
        { name: 'Packing', icon: '📦', path: '/ops/packing', roles: ['ops'] },
        { name: 'Delivery Challan', icon: '🚚', path: '/ops/ready-to-dispatch', roles: ['ops'] },
        { name: 'Quality Check', icon: '🔍', path: '/ops/quality-check', roles: ['ops'] },
        { name: 'In Transit', icon: '🚛', path: '/ops/in-transit', roles: ['ops'] },
        { name: 'Delivered', icon: '🏠', path: '/ops/delivered', roles: ['ops'] },
      ]
    },
    
    { 
      type: 'section', 
      name: 'Inwards', 
      roles: ['ops'],
      children: [
        { name: 'Inwards Que', icon: '📝', path: '/ops/inbound-que', roles: ['ops'] },
        { name: 'Pickup Challan', icon: '🚚', path: '/ops/pickup-challan', roles: ['ops'] },
        { name: 'Material In Checklist', icon: '📋', path: '/ops/mic', roles: ['ops'] },
        { name: 'Quality Check / Gate Pass', icon: '🔍', path: '/ops/inbound-qc', roles: ['ops'] },
        { name: 'Received', icon: '🏠', path: '/ops/received', roles: ['ops'] },
      ]
    },

    { 
      type: 'section', 
      name: 'Inventory', 
      roles: ['ops'],
      children: [
        { name: 'Inventory', icon: '📦', path: '/ops/inventory', roles: ['ops'] },
        { name: 'Inventory Transfer', icon: '🔄', path: '/ops/inventory-transfer', roles: ['ops'] },
      ]
    },

    // Accounts specific menu items
    { name: 'Credit Approval', icon: '💳', path: '/accounts/approvals', roles: ['accounts'] },
    { name: 'Accounts Dashboard', icon: '💰', path: '/', roles: ['accounts'] }, // Accounts dashboard is their root
    { name: 'Orders', icon: '📖', path: '/accounts/orders', roles: ['accounts'] },
    { name: 'Transaction Ledger', icon: '💸', path: '/accounts/transactions', roles: ['accounts'] },
    { name: 'Accounts Invoices', icon: '🧾', path: '/accounts/invoices', roles: ['accounts'] },
    // Admin specific menu items
    { name: 'Admin Dashboard', icon: '🛡️', path: '/admin', roles: ['admin'] },
    { name: 'Products', icon: '🛠️', path: '/admin/products', roles: ['admin'] },
    { name: 'System Settings', icon: '⚙️', path: '/admin/settings', roles: ['admin'] },
  ].filter(item => !item.roles || item.roles.includes(user?.role));

  const resultSectionStyle = {
    padding: '5px 0',
    borderBottom: '1px solid #e5e7eb'
  };

const resultHeaderStyle = {
  padding: '5px 15px',
  fontSize: '12px',
  fontWeight: '600',
  color: '#6b7280',
  textTransform: 'uppercase'
};

const resultItemStyle = {
  padding: '10px 15px',
  cursor: 'pointer',
};

const baseActionBtnStyle = {
  color: 'white', border: 'none', 
  padding: '8px 16px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
  fontSize: '14px', fontWeight: '500'
};

const dropdownLinkStyle = {
  padding: '12px 15px',
  textDecoration: 'none',
  color: '#374151',
  fontSize: '14px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  borderBottom: '1px solid #f3f4f6'
};

  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: 'Arial, sans-serif', backgroundColor: '#f3f4f6', overflow: 'hidden' }}>
      
      {/* SIDEBAR OVERLAY (Mobile) */}
      {windowWidth <= 768 && isSidebarOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1999
        }} />
      )}

      {/* SIDEBAR */}
      <div 
        ref={sidebarRef}
        style={{ 
          width: '250px', backgroundColor: '#1f2937', color: 'white', display: 'flex', flexDirection: 'column',
          position: windowWidth <= 768 ? 'fixed' : 'relative',
          height: '100vh',
          zIndex: 2000,
          left: isSidebarOpen ? 0 : '-250px',
          transition: 'left 0.3s ease'
        }}
      >
        <div style={{ padding: '20px', borderBottom: '1px solid #374151', display: 'flex', justifyContent: 'center', position: 'relative' }}>
          <img src={Logo} alt="NexBook Logo" style={{ maxWidth: '100%', height: 'auto' }} />
          {windowWidth <= 768 && (
            <button onClick={() => setIsSidebarOpen(false)} style={{ position: 'absolute', right: 10, top: 20, background: 'none', border: 'none', color: 'white' }}>
              <X size={20} />
            </button>
          )}
        </div>
        
        <nav style={{ flex: 1, padding: '10px', overflowY: 'auto' }}>
          {menuItems.map((item) => {
            if (item.type === 'section') {
              const isExpanded = expandedSections[item.name];
              return (
                <div key={item.name} style={{ marginBottom: '5px' }}>
                  <button
                    onClick={() => toggleSection(item.name)}
                    style={{
                      width: '100%',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 15px', background: 'none', border: 'none',
                      color: '#9ca3af', cursor: 'pointer', borderRadius: '6px',
                      transition: 'all 0.2s'
                    }}
                    className="sidebar-item-hover"
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{item.name}</span>
                    </div>
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  {isExpanded && (
                    <div style={{ paddingLeft: '10px' }}>
                      {item.children.map((child) => {
                        const isActive = location.pathname === child.path;
                        return (
                          <Link 
                            key={child.name} 
                            to={child.path} 
                            className="sidebar-item-hover"
                            style={{
                              display: 'flex', alignItems: 'center', gap: '10px',
                              padding: '10px 15px', textDecoration: 'none',
                              color: isActive ? 'white' : '#9ca3af',
                              backgroundColor: isActive ? '#374151' : 'transparent',
                              borderRadius: '6px', marginBottom: '2px',
                              fontSize: '14px',
                              transition: 'all 0.2s'
                            }}
                          >
                            <span style={{ fontSize: '16px' }}>{child.icon}</span>
                            {child.name}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const isActive = location.pathname === item.path;
            return (
              <Link 
                key={item.name} 
                to={item.path} 
                className="sidebar-item-hover"
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '12px 15px', textDecoration: 'none',
                  color: isActive ? 'white' : '#9ca3af',
                  backgroundColor: isActive ? '#374151' : 'transparent',
                  borderRadius: '6px', marginBottom: '5px',
                  transition: 'all 0.2s'
                }}
              >
                <span style={{ fontSize: '18px' }}>{item.icon}</span>
                {item.name}
              </Link>
            )
          })}
        </nav>

        <div style={{ padding: '20px', borderTop: '1px solid #374151' }}>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '10px' }}>
            Logged in as: {user.displayName}
          </div>
          <button 
            onClick={handleLogout}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'none', border: 'none', color: '#f87171', cursor: 'pointer' }}
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* TOP BAR */}
        <div style={{ 
          height: '60px', minHeight: '60px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', 
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: windowWidth <= 768 ? '0 15px' : '0 30px' 
        }}>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {windowWidth <= 768 && (
              <button 
                onClick={() => setIsSidebarOpen(true)}
                style={{ background: 'none', border: 'none', color: '#374151', cursor: 'pointer', padding: '5px' }}
              >
                <Menu size={24} />
              </button>
            )}

            {/* Search Bar (Hidden or Compact on small mobile) */}
            <div style={{ position: 'relative' }} ref={searchRef}>
              <div style={{ 
                display: 'flex', alignItems: 'center', backgroundColor: '#f3f4f6', padding: '8px 15px', borderRadius: '8px', 
                width: windowWidth <= 768 ? (windowWidth <= 480 ? '150px' : '250px') : '400px' 
              }}>
                <Search size={18} color="#9ca3af" />
                <input 
                  type="text" 
                  placeholder={windowWidth <= 480 ? "Search..." : 
                    (user?.role === 'ops' ? "Search orders..." : 
                     user?.role === 'accounts' ? "Search transactions, invoices..." : 
                     "Search quotes, invoices...")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => searchTerm.length >= 2 && setShowResults(true)}
                  style={{ border: 'none', background: 'transparent', marginLeft: '10px', outline: 'none', width: '100%' }}
                />
                {searchTerm && (
                  <X size={14} color="#9ca3af" style={{ cursor: 'pointer' }} onClick={() => setSearchTerm('')} />
                )}
              </div>

              {/* Search Results Dropdown */}
              {showResults && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  backgroundColor: 'white', border: '1px solid #e5e7eb',
                  borderRadius: '8px', marginTop: '5px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000, maxHeight: '400px', overflowY: 'auto'
                }}>
                  {isSearching ? (
                    <div style={{ padding: '15px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>Searching...</div>
                  ) : (
                    <>
                      {Object.keys(results).every(key => results[key].length === 0) ? (
                        <div style={{ padding: '15px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>No results found</div>
                      ) : (
                        <div style={{ padding: '10px 0' }}>
                          {results.quotes.length > 0 && (
                            <div style={resultSectionStyle}>
                              <div style={resultHeaderStyle}>Quotes</div>
                              {results.quotes.map(q => (
                                <div key={q.id} onClick={() => handleResultClick(`/quotes/${q.id}`)} style={resultItemStyle} className="hover-highlight">
                                  <div style={{ fontWeight: '600' }}>{q.quoteNumber || 'No Number'}</div>
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{q.customerName} - {q.subject}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {results.invoices.length > 0 && (
                            <div style={resultSectionStyle}>
                              <div style={resultHeaderStyle}>Invoices</div>
                              {results.invoices.map(i => (
                                <div key={i.id} onClick={() => handleResultClick(`/invoices/${i.id}`)} style={resultItemStyle} className="hover-highlight">
                                  <div style={{ fontWeight: '600' }}>{i.voucherNo || 'No Number'}</div>
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{i.customerName} - {i.subject}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {results.orders.length > 0 && (
                            <div style={resultSectionStyle}>
                              <div style={resultHeaderStyle}>Orders</div>
                              {results.orders.map(o => (
                                <div key={o.id} onClick={() => handleResultClick(`/orders/${o.id}`)} style={resultItemStyle} className="hover-highlight">
                                  <div style={{ fontWeight: '600' }}>{o.orderNumber || 'No Number'}</div>
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{o.customerName} - {o.subject}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Accounts Specific Search Results */}
                          {results.accounts_invoices.length > 0 && (
                            <div style={resultSectionStyle}>
                              <div style={resultHeaderStyle}>Accounts Invoices</div>
                              {results.accounts_invoices.map(i => (
                                <div key={i.id} onClick={() => handleResultClick(`/accounts/invoices?highlight=${i.id}`)} style={resultItemStyle} className="hover-highlight">
                                  <div style={{ fontWeight: '600' }}>{i.invoiceNo}</div>
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{i.customerName} - ₹{i.totalAmount.toLocaleString()}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {results.accounts_transactions.length > 0 && (
                            <div style={resultSectionStyle}>
                              <div style={resultHeaderStyle}>Transactions</div>
                              {results.accounts_transactions.map(t => (
                                <div key={t.id} onClick={() => handleResultClick(`/accounts/transactions?highlight=${t.id}`)} style={resultItemStyle} className="hover-highlight">
                                  <div style={{ fontWeight: '600' }}>{t.category}</div>
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{t.entityName} - ₹{t.amount.toLocaleString()} ({new Date(t.date).toLocaleDateString()})</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Quick Action */}
          {user?.role === 'sales' && (
            <div style={{ position: 'relative' }} ref={quickActionsRef}>
              {windowWidth <= 768 ? (
                <>
                  <button 
                    onClick={() => setShowSalesQuickActions(!showSalesQuickActions)}
                    className="action-btn-hover"
                    style={{ 
                      backgroundColor: '#2563eb', color: 'white', border: 'none', 
                      padding: '10px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    <Plus size={20} />
                  </button>
                  
                  {showSalesQuickActions && (
                    <div style={{
                      position: 'absolute', top: '100%', right: 0,
                      backgroundColor: 'white', border: '1px solid #e5e7eb',
                      borderRadius: '8px', marginTop: '5px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      zIndex: 1000, width: '160px', display: 'flex', flexDirection: 'column', overflow: 'hidden'
                    }}>
                      <Link to="/quotes/new" onClick={() => setShowSalesQuickActions(false)} style={dropdownLinkStyle} className="hover-highlight">
                        <Plus size={14} /> New Quote
                      </Link>
                      <Link to="/invoices/new" onClick={() => setShowSalesQuickActions(false)} style={dropdownLinkStyle} className="hover-highlight">
                        <Plus size={14} /> New Invoice
                      </Link>
                      <Link to="/orders/new" onClick={() => setShowSalesQuickActions(false)} style={dropdownLinkStyle} className="hover-highlight">
                        <Plus size={14} /> New Order
                      </Link>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Link to="/quotes/new" style={{ textDecoration: 'none' }}>
                    <button style={{ ...baseActionBtnStyle, backgroundColor: '#2563eb' }} className="action-btn-hover">
                        <Plus size={16} /> New Quote
                    </button>
                  </Link>
                  <Link to="/invoices/new" style={{ textDecoration: 'none' }}>
                    <button style={{ ...baseActionBtnStyle, backgroundColor: '#10b981' }} className="action-btn-hover">
                        <Plus size={16} /> New Invoice
                    </button>
                  </Link>
                  <Link to="/orders/new" style={{ textDecoration: 'none' }}>
                    <button style={{ ...baseActionBtnStyle, backgroundColor: '#f59e0b' }} className="action-btn-hover">
                        <Plus size={16} /> New Order
                    </button>
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Accounts Quick Actions */}
          {user?.role === 'accounts' && (
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                onClick={() => setShowAccTransModal(true)} 
                style={{ ...baseActionBtnStyle, backgroundColor: '#3b82f6' }} 
                className="action-btn-hover"
              >
                <Plus size={16} /> Add Transaction
              </button>
              <button 
                onClick={() => setShowAccInvModal(true)} 
                style={{ ...baseActionBtnStyle, backgroundColor: '#10b981' }} 
                className="action-btn-hover"
              >
                <Plus size={16} /> New Invoice
              </button>
            </div>
          )}

        </div>

        {/* PAGE CONTENT */}
        <div style={{ padding: windowWidth <= 768 ? '10px' : '30px' }}>
          {children || <Outlet />}
        </div>
      </div>

      {/* Modals for Accounts Quick Actions */}
      <TransactionModal 
        show={showAccTransModal} 
        onClose={() => setShowAccTransModal(false)} 
        db={db} 
        onSave={() => { /* Potentially refresh relevant accounts data */ setShowAccTransModal(false); }}
      />
      <AccountsInvoiceModal 
        show={showAccInvModal} 
        onClose={() => setShowAccInvModal(false)} 
        db={db} 
        onSave={() => { /* Potentially refresh relevant accounts data */ setShowAccInvModal(false); }}
      />
    </div>
  );
};

export default Layout;